import { selectors } from 'modules/common';
import { domain, stateTree } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';
import { useGetFields } from 'shared/data/webApp/v1';

import { createAccrualReport } from '../../../../actions';
import { ReportConfigExpanded } from '../../../../types/ReportConfig';
import { XeroAccrualReportFormData } from './FormPopupContent.types';

export const useFormData = (
    reportConfig: ReportConfigExpanded,
    accounts: {
        fieldValueId: string;
        value: string;
    }[]
) => {
    const dispatch = useDispatch();

    const form = useForm<Omit<XeroAccrualReportFormData, 'date'> & { date: Date }>({
        defaultValues: {
            narration: '',
            date: undefined,
            accrualAccountId: null,
        },
    });

    const isLoadingForm = useSelector((state) =>
        selectors.field.fieldSetIsLoading(state, {
            type: stateTree.FieldSetType.AllIntegrationFields,
            companyId: reportConfig.companyId,
        })
    );

    const handleSubmit = useCallback(
        async (data: Omit<XeroAccrualReportFormData, 'date'> & { date: Date }) => {
            const account = accounts.find((account) => account.fieldValueId === data.accrualAccountId);

            if (account) {
                dispatch(
                    createAccrualReport({
                        narration: data.narration,
                        account: { id: account.fieldValueId, text: account.value },
                        date: data.date,
                    })
                );
            }
        },
        [accounts, dispatch]
    );

    return {
        form,
        isLoadingForm,
        handleSubmitForm: form.handleSubmit(handleSubmit),
    };
};

export const useAccrualAccountData = (reportConfig: ReportConfigExpanded) => {
    const [query, setQuery] = useState('');
    const [queryValue, setQueryValue] = useState('');

    useDebounce(() => setQuery(queryValue), 600, [queryValue]);

    const integrationCode =
        reportConfig.reportCode === domain.ReportCode.XeroBillInvoiceLine
            ? domain.IntegrationCode.XeroBill
            : domain.IntegrationCode.XeroPo;

    const field = useSelector(
        (state) =>
            selectors.field.getFieldsBySystemPurpose(
                state,
                reportConfig.companyId,
                domain.FieldSystemPurpose.XeroAccount
            )[0]
    );

    const { data, isLoading } = useGetFields({
        query: {
            integrationCode,
            companyId: field.companyId,
            withValues: true,
            fieldsId: [field.id],
            query,
            startFrom: 0,
            rowNum: 116,
        },
    });

    const accrualAccounts = useMemo(() => {
        return data?.fields?.[0].exactValues || [];
    }, [data]);

    return {
        isLoadingAccrualAccounts: isLoading,
        accrualAccounts,
        onAccrualAccountsInputChange: setQueryValue,
    };
};
